/**
 * German (`de`) string catalogue — mirrors the key shape of `./en.ts`. Brand
 * names (`OVERVOLT`, `Sniper Rail`, `Arc Coil`, `Cryo Emitter`, `Splash
 * Mortar`, `Pulse Turret`, `Pylon`, `Flux`) remain in English where they
 * read as proper nouns; everything else translates idiomatically into
 * terse, gamey German.
 */

import type { Catalogue } from './en';

export const de: Catalogue = {
  meta: {
    name: 'Deutsch',
    code: 'DE',
  },

  towers: {
    pulse: {
      label: 'Pulse Turret',
      short: 'PULS',
      role: 'Neutraler Allrounder. Verlässlicher Schaden überall am Pfad.',
    },
    arc: {
      label: 'Arc Coil',
      short: 'ARC',
      role: 'An die Stromschiene gekoppelt. Stark im Pylon-Überschuss, schwach im Brownout.',
    },
    cryo: {
      label: 'Cryo Emitter',
      short: 'CRYO',
      role: 'Engstellen-Maler. Kleiner Radius — verlangsamt und markiert jeden Feind an einem Punkt.',
    },
    mortar: {
      label: 'Splash Mortar',
      short: 'MÖRSER',
      role: 'Indirektes Feuer. Stell ihn HINTER den Pfad — Nahbereich ist seine tote Zone.',
    },
    sniper: {
      label: 'Sniper Rail',
      short: 'SNIPER',
      role: 'Durchschlag in Sichtlinie. Stark auf langen Geraden; schwach in Ecken.',
    },
    pylon: {
      label: 'Pylon',
      short: 'PYLON',
      role: 'Energieversorgung. Genug bauen, um Türme zu speisen (mit Arc-freundlichem Überschuss).',
    },
    wall: {
      label: 'Wall Node',
      short: 'MAUER',
      role: 'Billiger Blocker — nur im Maze-Modus. Formt die Route der Feinde.',
    },
  },

  roleHints: {
    pulse: 'Pulse Turret: dein verlässlicher Standard — solider Schaden überall am Pfad.',
    arc: 'Arc Coil: skaliert mit Energieüberschuss. Bau extra Pylons für eine stärkere Kette.',
    cryo: 'Cryo Emitter: enges Engstellen-Mal. Verlangsamt und markiert die wenigen Felder am Choke.',
    mortar: 'Splash Mortar: indirektes Feuer. Stell ihn HINTER den Pfad — Nahbereich trifft er nicht.',
    sniper: 'Sniper Rail: Durchschlag in Sichtlinie. Richte ihn auf eine lange Gerade aus.',
    pylon: 'Power Pylon: speist das Netz. Bau genug, um die Türme zu versorgen.',
    wall: 'Mauer: billiger Blocker — formt die Route, die Feinde gehen müssen.',
  },

  hints: {
    build:
      'Bau einen Turm: wähl unten einen aus (oder drück 1) und klick ein leuchtendes Feld am Pfad.',
    placed: 'Gut. Bau noch einen und drück Leertaste, um die Welle zu starten.',
    wave1: 'Türme feuern automatisch. Halt die Energieleiste aus dem roten Bereich.',
    select: 'Klick einen platzierten Turm an — U für Upgrade, S zum Verkaufen.',
    brownout: 'Brownout! Türme gedrosselt. Bau einen Pylon (3) für mehr Energie.',
  },

  contextHints: {
    overload:
      'Overload: dreh die nächste Welle hoch für mehr Belohnung — bei höherem Risiko. Langsamer Start? Kurbel ihn an für schnelleren Flux.',
    profileKey: 'Drück P für dein Profil — Flux schaltet Türme, Fähigkeiten und Modi frei.',
    endlessMod: 'Endless rotiert Wellen-MODIFIKATOREN — passe deinen Aufbau an.',
    puzzleHover:
      'Puzzle-Modus: handgefertigte Logikrätsel mit Sichtlinien-Mauern. Komplett anderes Spiel.',
  },

  hud: {
    buildZone: 'BAU',
    waveZone: 'WELLE',
    abilitiesZone: 'FÄHIGKEITEN',
    overloadZone: 'OVERLOAD',

    startWave: 'WELLE STARTEN',
    nextWave: 'NÄCHSTE WELLE',
    pause: 'PAUSE',
    resume: 'WEITER',
    restart: 'NEUSTART',
    deployHint: 'start [SPACE]',
    holdHint: 'pause [SPACE]',
    rebootHint: 'reset [R]',
    nextWaveHint: 'NÄCHSTE WELLE',

    audioOn: 'TON AN',
    audioOff: 'TON AUS',

    credits: 'KREDITE',
    core: 'KERN',
    wave: 'WELLE',
    endless: 'ENDLESS',
    score: 'PUNKTE',
    powerGrid: 'STROMNETZ',

    runMode: 'MODUS',
    difficulty: 'SCHWIERIGKEIT',
    profile: 'PROFIL',
    map: 'KARTE',
    clickHint: '▷ klick',

    locked: 'GESPERRT',
    lockedShort: 'GESPERRT',
    lockedTooltip:
      'GESPERRT — schalte für ✦{cost} Flux im Profil frei (drück P).',
    lockedTowerNotice: '{tower} gesperrt',
    lockedAbility: 'Fähigkeit gesperrt',
    lockedOverload: 'Overload-Stufe gesperrt',
    lockedMode: 'Modus {mode} gesperrt',
    lockedDifficulty: 'Schwer gesperrt',

    cheatPip: '🔓 CHEAT',
    profilePip: '👤 {name}',

    overloadIdle: '— overload greift ab welle 1 —',
    overloadTier: 'TIER {tier} · {name}',
    overloadActive: '◉ AKTIV {sec}s',

    maxLevel: 'MAX. STUFE',
    upgrade: 'UPGRADE L{level}',
    sell: 'VERKAUFEN',
    target: 'ZIEL: {mode}',
    cycleTarget: 'wechseln · [T]',
    sellHint: '+{refund}c · [S]',
    upgradeHint: '{cost}c · [U]',
    overclock: 'OVERCLOCK',
    overclockOn: 'OVERCLOCK ✦ AN',

    // Sprint 69 (i18n-gaps-extract-s69) — extracted from render.ts / hud.ts.
    buildHintIntro: "BAUPHASE  //  wähl einen Turm [1·2·3·4·5], setz ihn und START WELLE [SPACE]",
    buildHintReinforce: "BAUPHASE  //  verstärk das Netz und START WELLE {wave} [SPACE]",
    mazeRoute: "L A B Y R I N T H - R O U T E",
    tiles: "FELDER",
    abilityActive: "◉ AKTIV {sec}s",
    costSuffix: '{cost}c',
    overclockPip: 'OC ×{n}',
    powerReadout: '{demand} / {supply}W  ·  {pct}%',
    towerCostDraw: '{cost}c {power}w',
    pylonCostSupply: '{cost}c +{supply}w',
    wallCost: '{cost}c 0w',
    endlessModBadge: 'MOD · {label}',
    startWaveOverload: '⚡{n} {tierName}',
    waveOverloadPip: '⚡{n} {tierName}',
    overclockNoticeOn: "{tower} Overclock AN",
    overclockNoticeOff: "{tower} Overclock AUS",

    tierMult: {
      hp: "HP",
      sp: "SP",
      cn: "AN",
      gd: "GD",
      sc: "SK",
    },
    spawnShort: {
      bit: "Bit",
      packet: "Pkt",
      daemon: "Dmn",
      fork: "Fork",
      cipher: "Cph",
      patch: "Pch",
      buffer: "Buf",
      streak: "Stk",
      kernel: "KERNEL",
    },
  },

  modes: {
    campaign: 'KAMPAGNE',
    endless: 'ENDLESS',
    maze: 'MAZE',
    puzzle: 'PUZZLE',
  },
  modeSub: {
    campaign: '10 wellen · gewinnen',
    endless: '✦ {cost}',
    maze: '✦ {cost}',
    puzzle: 'level wählen',
  },
  modeBlurb: {
    campaign: '10 Wellen. Boss in Welle 10. Gewinne und du hast bestanden.',
    endless: 'Gleiche Karte. Modifikator pro Welle. Passt euch an oder verliert.',
    maze: 'Offenes Feld. Ihr formt den Pfad mit Mauern. Bremst sie oder kürzt ihre Beute.',
    puzzle: '10 handgebaute Levels. Findet die eine Gewinnlösung.',
  },
  modeCard: {
    campaign: {
      tagline: 'Lerne das Netz, knacke den Boss.',
      bullet1: '10 handgetunte Wellen + Boss',
      bullet2: 'Feste S-Route — bauen, Welle, aufrüsten',
      bullet3: 'Sieg schaltet Endless auf dieser Karte frei',
      length: '~8 Min',
      skill: 'Trainiert Baureihenfolge',
    },
    endless: {
      tagline: 'Passt euch an, sonst leckt es. Kein Ende.',
      bullet1: 'Spielt Kampagne 1–10, dann skaliert ewig',
      bullet2: 'Neuer Wellenmodifikator jede Welle',
      bullet3: 'Lauf endet, wenn der Kern fällt',
      length: '~30 Min',
      skill: 'Trainiert Langzeit-Pivots',
    },
    maze: {
      tagline: 'Ihr baut das Labyrinth, das sie laufen.',
      bullet1: 'Offenes Feld — kein vorgezeichneter Pfad',
      bullet2: 'Türme + günstige Mauern formen die Route',
      bullet3: 'Längere Route = mehr Zeit im Feuer',
      length: '~12 Min',
      skill: 'Trainiert räumliches Routing',
    },
    puzzle: {
      tagline: 'Eine Lösung gewinnt. Findet sie.',
      bullet1: '10 handgebaute Levels, einzige Lösung',
      bullet2: 'Festes Budget, feste Welle',
      bullet3: 'Sichtlinien-Mauern + Turmlimits',
      length: 'je ~2 Min',
      skill: 'Trainiert Deduktion',
    },
  },
  difficulty: {
    easy: 'LEICHT',
    normal: 'NORMAL',
    hard: 'SCHWER',
  },
  difficultySub: {
    easy: 'mild',
    normal: 'ausgewogen',
    hard: '✦ {cost}',
  },

  overlay: {
    paused: 'PAUSE',
    pauseHint: 'DRÜCK  LEERTASTE  ZUM  WEITERMACHEN',

    missionComplete: '// MISSION ERFÜLLT',
    gridSecured: 'NETZ GESICHERT',
    wavesRepelled: 'Alle {total} Angriffswellen abgewehrt.',
    continueEndless: 'WEITER  →  ENDLESS',
    unlockEndless: 'schalte Endless im Profil frei, um weiterzumachen',
    restartCampaign: 'oder drück  R  für einen Kampagnen-Neustart',

    systemFailure: '// SYSTEMAUSFALL',
    coreBreached: 'KERN DURCHBROCHEN',
    endlessEnded: 'Endless-Lauf endete bei Welle {wave}.',
    reactorOverrun: 'Reaktor überrannt in Welle {wave} von {total}.',
    runItBack: 'DRÜCK  R  FÜR  REVANCHE',

    finalScore: 'ENDPUNKTZAHL',
    newHighScore: '★  NEUER HIGHSCORE  ★',
    newEndlessBest: '★  NEUE ENDLESS-BESTLEISTUNG  ★',
    highScore: 'HIGHSCORE',
    endlessBest: 'ENDLESS-BEST',

    // Sprint 69 — top-banner countdown overlays drawn by render.ts.
    aftershock: "// NACHBEBEN — NETZ-TRIBUT  {sec}s //",
    overcharge: "// OVERCHARGE — NETZ GEPUSCHT  {sec}s //",
    brownout: "// NETZ-BROWNOUT — TÜRME GEDROSSELT //",
  },

  profile: {
    title: 'PROFIL',
    subtitle: '// META-FORTSCHRITT',
    closeHint: 'drück  P  oder  ESC  zum Schließen',
    tabUnlocks: 'FREISCHALTUNGEN',
    tabScoreboard: 'RANGLISTE',
    flux: 'FLUX',
    unlockTree: 'FREISCHALT-BAUM',
    unlockedCount: '{owned} / {total} FREI',
    lifetime: 'GESAMT',

    branchArsenal: 'ARSENAL',
    branchModes: 'MODI',
    branchChallenge: 'HERAUSFORDERUNG',
    branchCosmetic: 'KOSMETIK',

    statRunsPlayed: 'LÄUFE GESPIELT',
    statCampaignWins: 'KAMPAGNEN-SIEGE',
    statBestScore: 'BESTE PUNKTZAHL',
    statDeepestEndless: 'TIEFSTES ENDLESS',
    statTotalFlux: 'FLUX GESAMT',

    personalBests: 'PERSÖNLICHE BESTEN',
    waveLabel: 'WELLE {wave}',

    runHistory: 'LAUFVERLAUF',
    noRuns: 'Noch keine Läufe — beende einen Lauf und beginn den Aufstieg.',
    earlierRuns: '+ {n} älterer Lauf',
    earlierRunsPlural: '+ {n} ältere Läufe',
    colRun: 'LAUF',
    colMode: 'MODUS · SCHWIERIGKEIT',
    colResult: 'ERGEBNIS',
    colOverload: 'OVL',
    colScore: 'PUNKTE',
    colDeltaBest: 'Δ BEST',
    colFlux: 'FLUX',
    starBest: '★ BEST',

    statusUnlocked: 'FREI',
    statusOwned: 'IM BESITZ',
    statusRequiresPrereq: 'Erst {prereq}',
    statusFluxShort: 'Brauche ✦{short} mehr Flux',
    statusAffordable: 'Verfügbar — klick zum Freischalten',
    statusCost: '✦{cost} zum Freischalten',
    buyAction: 'KAUFEN  ▸',
    nodeReq: 'BED: {prereq}',

    profileLauncher: 'PROFIL',
    profileKeyHint: '[ P ]',
    viewScoreboard: 'RANGLISTE  ANZEIGEN',
    newPersonalBest: '★  NEUE PERSÖNLICHE BESTLEISTUNG  ★',
    fromYourBest: 'VON DEINEM BESTWERT',

    grantTower: 'WAFFENTURM',
    grantAbility: 'AKTIVE FÄHIGKEIT',
    grantMode: 'SPIELMODUS',
    grantDifficulty: 'SCHWIERIGKEIT',
    grantOverload: 'OVERLOAD-STUFE {tier}',
    grantTheme: 'BRETT-DESIGN',

    // Sprint 69 — unlock-tree hover preview.
    nodePreview: {
      towerMortar: "Indirektes Feuer. Stell ihn HINTER den Pfad — Nahbereich tot; arc-shells auf Cluster, schreddert Panzerung.",
      towerSniper: "Sichtlinien-Lanze. Auf eine lange Gerade — jeder Feind in der Linie trifft’s; in Ecken schwach.",
      abilityAirstrike: "Einzelschuss-Blast auf ein Feld (45c). Räum einen Cluster oder knapp einen Brocken an.",
      abilityEmp: "Feldweiter Stun (80c). Frier alle Feinde kurz ein, wenn die Bahn überrannt ist.",
      modeEndless: "Spiel über Welle 10 hinaus — prozedurale Wellen bis der Kern fällt. Neuer Score-Track.",
      modeMaze: "Offenes Routenbau-Mode. Türme blockieren ihr Feld; Feinde A*-um sie herum.",
      difficultyHard: "Härtere Wellen; größere Gold- und Score-Multiplikatoren. Schaltet das obere Flux-Band frei.",
      overloadCritical: "Overload Tier 3 — dreh es hoch für fettere Score/Flux-Multiplikatoren in der nächsten Welle.",
      overloadMeltdown: "Overload Tier 4 — die härteste Pre-Set-Welle. Top-Multiplikatoren; nur für solide Builds.",
      themeAmber: "Kosmetische Palette — warmes Amber-CRT-Terminal. Rein kosmetisch; kein Spieleffekt.",
      themePhosphor: "Kosmetische Palette — Vintage-Grün-Phosphor. Rein kosmetisch; kein Spieleffekt.",
    },
  },

  help: {
    eyebrow: '/ /  R E F E R E N Z',
    title: 'BETRIEBSHANDBUCH',
    controls: 'STEUERUNG',
    powerGrid: 'DAS STROMNETZ',
    powerGridBody:
      'Waffen brauchen Energie; Pylons liefern sie. Übersteigt der Bedarf die Versorgung, fällt das Netz in Brownout und alle Türme verlangsamen — bau Pylons, damit sie weiterfeuern.',
    objective: 'ZIEL',
    objectiveBody:
      'Räumt alle 15 Kampagnenwellen ab; ihr verliert in dem Moment, in dem die Leben des Kerns auf null fallen. Der Endless-Modus läuft endlos.',
    mazeMode: 'MAZE-MODUS',
    mazeModeBody:
      'Ein dritter Modus auf offenem Feld: Türme blockieren ihr Feld und Feinde umgehen sie per A*, also baut ihr das Labyrinth. Den Kern dürft ihr nie komplett zumauern.',
    glitches: 'GLITCHES',
    towers: 'TÜRME',
    abilities: 'FÄHIGKEITEN',
    replayIntro: '↺  INTRO ERNEUT',
    closeHint: 'Esc / H / ? oder Klick irgendwo schließt',
  },

  intro: {
    eyebrow: '/ /  S Y S T E M - B R I E F I N G',
    titleBig: 'OVERVOLT',
    subtitle: 'Verteidige den Kern',
    line1:
      'Korrumpierte Programme marschieren über den Schaltkreis zu deinem Kern. Bau Türme, um sie aufzuhalten, bevor sie durchbrechen.',
    line2:
      '⚡ Jeder Turm braucht Energie. Bau zu viel und das Netz fällt in Brownout — alle Türme verlangsamen auf einmal. Bau Pylons, damit der Strom fließt.',
    line3: 'Bau in der Ruhephase, drück dann Welle starten. Überlebe 15 Wellen zum Sieg.',
    line4: 'Maus baut · 1–5 wählen · Leertaste start/pause · U upgrade · S verkaufen',
    gotIt: 'KAPIERT  →',
    gotItHint: 'drück eine beliebige Taste',
  },

  puzzle: {
    label: 'PUZZLE',
    levels: {
      'p0-first-light': {
        name: 'Erstes Licht',
        hint: 'Setz einen Pulse neben den Korridor. Jedes Feld in Reichweite reicht.',
      },
      'p1-long-lance': {
        name: 'Lange Lanze',
        hint: 'Ein Sniper auf einer Linie mit der Bahn durchschlägt jeden Feind.',
      },
      'p2-over-the-wall': {
        name: 'Über die Mauer',
        hint: 'Die Mauer blockt jeden direkten Strahl. Mörsergranaten fliegen drüber.',
      },
      'p3-funhouse-mirror': {
        name: 'Zerrspiegel',
        hint: 'Ein Sniper durch die Lücke trifft beide Bahnen.',
      },
      'p4-surplus-riches': {
        name: 'Reicher Überschuss',
        hint: 'Bau Pylons über das Freiband hinaus. Der Arc Coil lebt vom Überschuss.',
      },
      'p5-pinch-point': {
        name: 'Engstelle',
        hint: 'Cryo malt den Choke; Pulses ringsum machen den Rest.',
      },
      'p6-cipher-gauntlet': {
        name: 'Cipher-Spiessrutenlauf',
        hint: 'Mörser zerreißt die Panzerung; Cryo malt die Kurve; Sniper durchstößt das untere Bein. Nutze die ganze Truppe.',
      },
      'p7-kernel-echo': {
        name: 'Kernel-Echo',
        hint: 'Mörser lobt über die Sichtlinien-Flanken. Sniper schlägt von unten durch. Achte auf den Power Drain.',
      },
      'p8-overclock-crunch': {
        name: 'Overclock-Drang',
        hint: 'Ein Pulse reicht nicht. Overclocke das Burst-Fenster — Dauerfeuer genügt nicht.',
      },
      'p9-pylon-pinch': {
        name: 'Pylon-Enge',
        hint: 'Pack Pylons zwischen die Türme — die Aura-Grenze belohnt enge Baufelder.',
      },
    },
    briefing: {
      eyebrow: "PUZZLE-BRIEFING",
      startBtn: "Start ▷",
      cancelBtn: "Abbrechen",
      sectionConstraints: "EINSCHRÄNKUNGEN",
      sectionTowers: "TÜRME",
      sectionWinCondition: "SIEGBEDINGUNG",
      budgetLabel: "Gold-Budget",
      wavesLabel: "Wellen",
      towerAllowed: "ERLAUBT",
      towerForbidden: "GESPERRT",
      resultEyebrowWon: "PUZZLE GELÖST",
      resultEyebrowLost: "PUZZLE GESCHEITERT",
      resultTitleWon: "GELÖST!",
      resultTitleLost: "NOCHMAL VERSUCHEN",
      resultStatTowers: "TÜRME",
      resultStatGold: "GOLD AUSGEGEBEN",
      resultStatLives: "LEBEN ÜBRIG",
      resultNextBtn: "Nächstes Level ▷",
      resultReplayBtn: "Wiederholen",
      resultResetBtn: "Versuch zurücksetzen",
      resultBackBtn: "Zurück zur Auswahl",
    },

    // Sprint 69 — puzzle level-select overlay chrome.
    select: {
      subtitle: "Handgebaute Logik-Level — finde die eine Gewinnlösung.",
      close: "Schließen",
      cardTitle: 'P{index}  {name}',
      lockedCard: "🔒 GESPERRT",
      solved: "✓ Gelöst",
      unsolved: "▷ Ungelöst",
      noSolve: "Noch keine Lösung",
      bestLine: "Best: {towers}t · {gold}g · {power}p",
      allowed: "Erlaubt: {list}",
      parPlaceholder: "par: — / — / —",
      parRow: "{towers}t · {gold}g · {power}p",
    },
  },

  localeChip:
 {
    label: 'SPRACHE',
    en: 'EN',
    cs: 'CS',
  },

  resume: {
    title: 'Vorigen Lauf fortsetzen?',
    resume: 'Fortsetzen',
    newRun: 'Neuer Lauf',
    hint: '[Enter] Fortsetzen   ·   [Esc] Neu',
    detail: "{mode} · {difficulty} · welle {wave} · {towers} türme · {gold}g · {lives}♥",
  },

  // -------- Sprint 69: Kernel boss UI chrome ---------------------------------
  boss: {
    kernelHeader: "⚠  DER KERNEL",
    phase: "PHASE {phase} · {name}",
    powerDrain: "⚡ POWER DRAIN",
    kernelIncoming: "⚠  KERNEL  KOMMT  ⚠",
    phaseName: {
      boot: "BOOT SEQUENCE",
      fork: "FORK BOMB",
      panic: "KERNEL PANIC",
    },
  },

  // -------- Sprint 69: Map registry display strings --------------------------
  maps: {
    default: { label: "Datawash", tagline: "Offener S-Bogen — der klassische OVERVOLT-Aufbau." },
    cramped: { label: "Switchyard", tagline: "Enge Bahnen — jedes Feld ist umkämpft." },
    lines: { label: "Long Lines", tagline: "Lange Geraden — die Lanze führen." },
    serpentine: { label: "Snake Run", tagline: "Enge Serpentine — Cryo-Maler glänzt." },
  },

  // -------- Sprint 69: Ability display names ---------------------------------
  abilities: {
    overcharge: { label: 'Overcharge' },
    airstrike: { label: 'Airstrike' },
    empBurst: { label: 'EMP Burst' },
  },

  // -------- Sprint 69: Overload tier names -----------------------------------
  overload: {
    tierName: {
      nominal: "Nominal",
      charged: "Geladen",
      surging: "Anschwellend",
      critical: "Kritisch",
      meltdown: "Meltdown",
    },
  },

  // -------- Sprint 69: Endless modifier labels -------------------------------
  endlessMod: {
    armoredSurge: { label: "Gepanzerter Schub" },
    fastPack: { label: "Schnelles Pack" },
    scatteredSpread: { label: "Streuung" },
    streakRunners: { label: "Streak-Läufer" },
    daemonRush: { label: "Daemon-Sturm" },
  },

  // -------- Sprint 69: Enemy display names (Help roster) ---------------------
  enemies: {
    bit: { label: "Bit" },
    packet: { label: "Packet" },
    daemon: { label: "Daemon" },
    fork: { label: "Fork" },
    cipher: { label: "Cipher" },
    patch: { label: "Patch" },
    buffer: { label: "Buffer" },
    streak: { label: "Streak" },
    kernel: { label: "Kernel" },
  },

  // -------- Sprint 69: Help-panel enemy one-liners ---------------------------
  enemyRole: {
    bit: "Basis-Kanonenfutter — billig, langsam, zerbrechlich.",
    packet: "Schnell und schwach; rennt zum Kern.",
    daemon: "Schwerer HP-Schwamm; trifft hart bei einem Leak.",
    fork: "Spaltet sich beim Tod in zwei Bits.",
    cipher: "Gepanzert — knack ihn mit Mörser-Korrosion.",
    patch: "Heilt nahe Glitches; nimm ihn zuerst raus.",
    buffer: "Regenerativer Schild; brich ihn mit Burst-Schaden.",
    streak: "Sprintet allein; resistent gegen Slow. Deck den Pfad ab.",
    kernel: "Phasen-Boss — saugt deine Energie. Burst ihn vor der nächsten Phase.",
  },

  // -------- Sprint 69: Help-panel ability one-liners -------------------------
  abilityRole: {
    overcharge: "Push das Netz: jeder Turm feuert kurz schneller. Plan ihn um eine schwere Welle.",
    airstrike: "Einzelschuss-Blast auf ein Zielfeld. Cluster räumen oder einen Brocken anknabbern.",
    empBurst: "Feldweiter Stun — alle Feinde frieren kurz ein. Kauf dir Zeit, wenn die Bahn überrannt ist.",
  },

  // -------- Sprint 69: Tooltip chrome ----------------------------------------
  tooltip: {
    levelOf: "LV {level}/{max}",
    credits: "{cost} Kredits",
    next: "NÄCHSTE  →  LV {level}   {cost} Kredits",
    cooldown: "COOLDOWN  {sec}s",
    stat: {
      supply: "VERSORGUNG  +{n}w ins Netz",
      dmgRngCd: "DMG {damage}    RNG {range}    CD {cd}s",
      pwrChain: "PWR {power}w    KETTE {chain}",
      pwrAoe: "PWR {power}w    AOE {targets} Ziele",
      slow: "SLOW ×{factor}  für  {sec}s",
      brittle: "SPRÖDE +{pct}% dmg von anderen Türmen",
      pwrBlast: "PWR {power}w    BLAST {tiles} Felder",
      pwrSingle: "PWR {power}w    1 Ziel",
    },
  },

  // -------- Sprint 69: Help-panel control-row labels -------------------------
  controls: {
    arm: "Wähle Pulse / Arc / Pylon / Cryo / Mörser / Sniper",
    mouse: "Auf Feld platzieren · Turm anklicken zum Wählen",
    space: "Welle starten · Welle pausieren",
    fast: "Vorspulen (1× ⇄ 2×)",
    upgradeSell: "Gewählten Turm upgraden / verkaufen",
    target: "Zielmodus des gewählten Turms wechseln",
    profile: "Profil öffnen (Freischalt-Baum · Rangliste)",
    utility: "Abbrechen · stumm · Lauf neu starten",
  },

  // -------- Sprint 69: Unlock-tree node display names ------------------------
  unlocks: {
    towerMortar: { name: "Splash Mortar" },
    towerSniper: { name: "Sniper Rail" },
    abilityAirstrike: { name: "Airstrike" },
    abilityEmp: { name: "EMP Burst" },
    modeEndless: { name: "Endless-Modus" },
    modeMaze: { name: "Maze-Modus" },
    difficultyHard: { name: "Schwere Schwierigkeit" },
    overloadCritical: { name: "Overload: Critical" },
    overloadMeltdown: { name: "Overload: Meltdown" },
    themeAmber: { name: "Amber-Terminal" },
    themePhosphor: { name: "Phosphor-Grün" },
  },

  // Sprint 73 — interaktives Tutorial (docs/TUTORIAL_DESIGN.md §6).
  tutorial: {
    button: {
      start: 'TUTORIAL STARTEN',
      startSub: 'Zum ersten Mal? Tour starten →',
      replay: 'TOUR WIEDERHOLEN',
      replaySub: 'Grundlagen auffrischen',
    },
    step: {
      welcome: {
        title: 'Willkommen bei OVERVOLT',
        body: 'Du bist die Verteidigungs-KI eines defekten Reaktors. Korrupte Programme marschieren über die Schaltung zu deinem Core — vernichte sie, bevor sie durchbrechen.',
      },
      path: {
        title: 'Der Pfad',
        body: 'Feinde folgen immer dieser S-Kurve, vom Spawn-Feld links bis zum Core rechts. Du entscheidest, wo die Türme stehen.',
      },
      firstPlace: {
        title: 'Setze deinen ersten Turm',
        body: 'Eine Pulse Turret kostet 50 Credits und zieht 4 Strom. Klicke das leuchtende Feld neben dem Pfad.',
      },
      wave1: {
        title: 'Schicke die erste Welle',
        body: 'Drücke WELLE STARTEN. Deine Pulse feuert automatisch auf alles in Reichweite.',
      },
      towerRoles: {
        title: 'Fünf Türme, fünf Rollen',
        body: 'Pulse: zuverlässig überall. Sniper: lange Geraden. Mörser: indirektes Feuer, ABSEITS vom Pfad. Arc: skaliert mit Stromüberschuss. Cryo: malt einen Engpass mit Slow + Brittle.',
      },
      powerGrid: {
        title: 'Das Stromnetz',
        body: 'Jeder Turm zieht Strom. Wenn Bedarf das Angebot übersteigt, brownt das Netz aus und alle Türme verlangsamen. Baue Pylonen — benachbarte Pylonen verstärken die Feuerrate.',
      },
      overloadDial: {
        title: 'Overload — Risiko vs Belohnung',
        body: 'Drehe den Regler hoch für die nächste Welle: härtere Feinde, mehr Flux. Dreh ihn runter, um sicher zu spielen.',
      },
      overclock: {
        title: 'Turm übertakten',
        body: 'OVERCLOCK an einem gewählten Turm für ein Burst-Fenster: schnelleres Feuer, mehr Stromzug. Burst-Gewinn, Steady-State-Verlust.',
      },
      abilities: {
        title: 'Aktive Fähigkeiten',
        body: 'Drei Slots: Overcharge boostet das Netz. Airstrike legt eine bleibende Brandzone. EMP Burst betäubt alle Feinde. Plane sie — nicht panisch drücken.',
      },
      mazePreview: {
        title: 'Maze-Modus',
        body: 'Kein fester Pfad — Türme blockieren ihre Felder und Feinde A*-routen drumherum. Du baust den Weg, den sie laufen müssen.',
      },
      puzzlePreview: {
        title: 'Puzzle-Modus',
        body: 'Handgemachte Logik-Level mit Sichtlinien-Wänden. Festes Budget, feste Welle — finde das eine gewinnende Layout. Ein anderes Spiel.',
      },
      wrapUp: {
        title: 'Du bist bereit',
        body: 'Runs verdienen FLUX — gib es im Profil aus für neue Türme, Fähigkeiten und Modi. Beginne mit der Kampagne; komm zurück für Endless, Maze und Puzzle.',
      },
    },
    ui: {
      eyebrow: 'SCHRITT {n} / {total}',
      next: 'WEITER →',
      back: '← ZURÜCK',
      skip: 'TOUR ÜBERSPRINGEN',
      play: 'SPIELEN  ▸',
      skipConfirm: 'Tour überspringen? Du kannst sie jederzeit vom Titelbildschirm aus wiederholen.',
    },
  },
};
